<template>
  <v-form
    ref="form"
    aria-autocomplete="off"
    autocomplete="off"
    @keyup.enter="submit"
    @submit.prevent
  >
    <!-- PromoCode Eddor Dialog -->
    <confirmation-dialog
      v-if="showPromoAlert"
      :visible="showPromoAlert"
      :message="promoAlertMessage"
      :cancel-title="$t('message.promo_code_proceed_without')"
      :confirm-title="$t('message.promo_code_reenter')"
      disable-esc
      @cancel="promoCode = ''; submit();"
      @close="showPromoAlert=false"
    />
    <!-- Registration Form -->
    <v-card-title class="text-h5">
      {{ $t('title.register_start_using_guestplan') }}
    </v-card-title>
    <v-divider />
    <v-card-text class="pa-5 pb-0">
      <validation-observer ref="observer">
        <v-row class="pa-md-5 pa-2 pt-0">
          <v-col
            cols="12"
            md="6"
            class="py-1"
          >
            <validated-text-field
              v-model="fullName"
              rules="required|min:1|max:255"
              autocomplete="new-password"
              aria-autocomplete="off"
              role="presentation"
              autofocus
              :label="$t('label.your_full_name')"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
            class="py-1"
          >
            <validated-text-field
              v-model="businessName"
              rules="required|min:1|max:255"
              autocomplete="new-password"
              aria-autocomplete="off"
              role="presentation"
              :label="$t('label.name_of_your_business')"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-1"
          >
            <validated-text-field
              v-model="email"
              rules="required|max:254|email"
              autocomplete="new-password"
              aria-autocomplete="off"
              role="presentation"
              :label="$t('label.email')"
              type="email"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-1"
          >
            <validated-text-field
              v-model="phone"
              rules="max:50|phone"
              class="pt-0"
              :label="$t('label.phone_number')"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-1"
          >
            <password-text-field
              v-model="password"
              :label="$t('label.password')"
            />
          </v-col>
          <v-col
            v-if="showPromo"
            cols="12"
            class="py-1"
          >
            <validated-text-field
              v-model="promoCode"
              rules="max:128"
              :label="$t('label.promo_code')"
            />
          </v-col>

          <v-col
            cols="12"
            class="pt-5"
          >
            <cfturnstile
              ref="turnstileRef"
              sitekey="0x4AAAAAACewtdY15cVdpWB-"
              :size="$vuetify.breakpoint.xsOnly ? 'compact' : 'flexible'"
              :theme="$vuetify.theme.dark ? 'dark' : 'light'"
              @verify="verifyToken"
            />
            <v-btn
              color="success"
              block
              large
              :disabled="!turnstileToken"
              @click="submit"
            >
              {{ $t('button.create_free_account') }}
            </v-btn>
            <i18n
              path="label.register_tos_acklowledge"
              tag="p"
              class="pa-0 ma-0 text-center pt-2"
            >
              <a
                href="https://www.guestplan.com/tos"
                target="_blank"
              >{{ $t('label.tos') }}</a>
              <a
                href="https://www.guestplan.com/privacy"
                target="_blank"
              >{{ $t('label.privacy_policy') }}</a>
            </i18n>
          </v-col>
        </v-row>
      </validation-observer>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        color="error"
        text
        @click="close"
      >
        {{ $t('button.close') }}
      </v-btn>
    </v-card-actions>
  </v-form>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { ValidationObserver } from 'vee-validate';
import { performAction } from '@/services/vue-utils';
import PasswordTextField from '@/components/forms/PasswordTextField.vue';
import { startAutoUpdates } from '@/services/autoupdater';
import { ApiError, ApiErrorCode } from '@/api/api-error';
import toast from '@/plugins/toast-plugin/toast';
import TurnstileComponent from '@/components/common/TurnstileComponent.vue';

export default Vue.extend({
  name: 'RegisterAccountStep',
  components: { PasswordTextField, cfturnstile: TurnstileComponent },
  props: { },
  data() {
    return {
      state: 'enter-details',
      fullName: '',
      businessName: '',
      email: '',
      password: '',
      phone: '',
      promoCode: this.$route.params.code,
      showPromoAlert: false,
      promoAlertMessage: '',
      turnstileToken: undefined as undefined | string,
    };
  },
  computed: {
    showPromo(): boolean {
      return this.$route.name === 'promo';
    },
  },
  watch: {
    dialog(value, oldValue) {

    },
  },
  methods: {
    verifyToken(resp: string) {
      this.turnstileToken = resp;
      console.log(`captcha token: ${resp}`);
    },
    reset() {
      this.fullName = '';
      this.businessName = '';
      this.email = '';
      this.password = '';
      this.phone = '';
      this.state = 'enter-details';

      (this.$refs.form as HTMLFormElement).reset();
      (this.$refs.observer as InstanceType<typeof ValidationObserver>).reset();
    },
    async close() {
      this.reset();
      this.$emit('close');
    },
    async submit() {
      const isValid = await (this.$refs.observer as InstanceType<typeof ValidationObserver>).validate();
      if (!isValid) {
        toast.error(this.$i18n.tc('error.validation_failed'));
        return;
      }

      const { locale } = this.$i18n;

      let promoOk = true;
      const success = this.$i18n.tc('message.registration_code_sent_to_x_please_enter_code', undefined, [this.email]);
      const ok = await performAction(
        '',
        this.$i18n.tc('error.registration_failed'),
        async () => {
          try {
            await this.$tstore.dispatch('openAccount', {
              username: this.email,
              password: this.password,
              firstname: this.fullName,
              companyName: this.businessName,
              phone: this.phone,
              locale,
              promoCode: this.promoCode || undefined,
              turnstileToken: this.turnstileToken,
            });
            await startAutoUpdates();
            toast.success(success);
          } catch (e) {
            this.turnstileToken = undefined;
            (this.$refs.turnstileRef as any)?.reset();

            const ae = e as ApiError;
            if ([ApiErrorCode.promo_code_not_valid, ApiErrorCode.promo_code_already_exists].includes(ae.code)) {
              promoOk = false;
              this.promoAlertMessage = this.$i18n.tc('message.promo_code_not_valid');
              if (ae.code === ApiErrorCode.promo_code_already_exists) {
                this.promoAlertMessage = this.$i18n.tc('message.promo_code_already_exists');
              }
              this.showPromoAlert = true;
            } else {
              throw e;
            }
          }
        },
      );
      if (ok && promoOk) {
        this.$emit('account-created');
      }
    },
  },
});
</script>
